/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectAccess.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  CustomTextFieldN,
  SearchSelectProjectTextField,
} from "../../components";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import NewRequestTab from "./project-access/NewRequestTab";
import PendingRequestsTab from "./project-access/PendingRequestsTab";
import AccessControlTab from "./project-access/AccessControlTab";

import { useLocation } from "react-router-dom";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import appTheme from "../../assets/AppTheme/appTheme";
import CloseIcon from "@mui/icons-material/Close";
import {
  HandleApiActions,
  labCar,
  userManagementApis,
} from "../../redux/actions";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

function ProjectAccess() {
  const moduleTypes = {
    PROJECT: "PROJECT",
    VARIANT: "VARIANT",
    EE: "EE",
    SWM: "SWM",
    VEHICLE_SYSTEM: "VS",
    VEHICLE_COMPONENT: "VC",
  };
  const accessTypes = { COOWNER: "OWNER", EDITOR: "EDITOR", VIEWER: "VIEWER" };

  const dispatch = useDispatch();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get("tab");

  const [page] = useState(1);
  const [rowsPerPage] = useState(25);
  const [selectedRowData] = useState(null);
  const [openApproveAccessDialog, setOpenApproveAccessDialog] = useState(false);

  const [reasonToApproveAccess, setReasonToApproveAccess] = useState("");

  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );

  const {
    project_status,
    access_module_types,
    access_request_types,
    access_request_status,
  } = useSelector((state) => state?.settingsReducer?.metaData, shallowEqual);

  const [selectedPaTab, setSelectedPaTab] = useState(0);

  useEffect(() => {
    if (tabFromUrl === "AC") {
      setSelectedPaTab(2);
    }
  }, [tabFromUrl]);

  const [openForwardApproveAccessDialog, setOpenForwardApproveAccessDialog] =
    useState(false);
  const [selectedApproveAccessData, setSelectedApproveAccessData] =
    useState(null);

  const [selectedRejectAccessData, setSelectedRejectAccessData] =
    useState(null);
  const [reasonToRejectAccess, setReasonToRejectAccess] = useState("");
  const [openRejectAccessDialog, setOpenRejectAccessDialog] = useState(false);
  const [triggerFetchUser, setTriggerFetchUser] = useState(true);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...labCar.get_project_details,
        params: {
          page_number: 1,
          page_size: 10000,
          sort_column: "created_at",
          sort_order: "DESC",
        },
      }),
    );
  }, [dispatch]);

  const fetchUserDetails = useCallback(async () => {
    try {
      const apiDetails = userManagementApis?.get_users_list_pa;
      delete apiDetails.action_type;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              page_size: 100,
              page_number: 1,
            },
            show_toast: false,
          }),
        ),
      );

      if (!resp?.error) {
        // user details retrieved
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  }, [dispatch]);

  const getRequestAccessList = useCallback(async () => {
    let apiDetails = userManagementApis.get_forward_access;

    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
        }),
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    getRequestAccessList();
  }, [getRequestAccessList, triggerFetchUser]);

  const handleConfirmRejectAccess = () => {
    rejectForwardAccess(selectedRejectAccessData);
    setTimeout(() => {
      getRequestAccessList();
    }, 500);
    setOpenRejectAccessDialog(false);
    setSelectedRejectAccessData(null);
    setReasonToRejectAccess("");
  };

  const handleCancelRejectAccess = () => {
    setOpenRejectAccessDialog(false);
    setReasonToRejectAccess("");
  };

  const handleConfirmApproveAccess = () => {
    approveForwardAccess(selectedApproveAccessData);
    setTimeout(() => {
      getRequestAccessList();
    }, 500);
    setOpenApproveAccessDialog(false);
    setSelectedApproveAccessData(null);
    setReasonToApproveAccess("");
  };



  const approveForwardAccess = async (access_request_id) => {
    let apiDetails = userManagementApis.approve_forward_access;
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            access_request_id: access_request_id,
            update_reason: reasonToApproveAccess,
          },
          show_toast: true,
        }),
      ),
    );
  };

  const rejectForwardAccess = async (access_request_id) => {
    let apiDetails = userManagementApis.reject_forward_access;
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            access_request_id: access_request_id,
            update_reason: reasonToRejectAccess,
          },
          show_toast: true,
        }),
      ),
    );
  };



  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails, rowsPerPage, page, triggerFetchUser]);

  const handleCloseApproveAccessDialog = () => {
    setOpenForwardApproveAccessDialog(false);
  };

  const handleCancelApproveAccess = () => {
    setOpenApproveAccessDialog(false);
    setReasonToApproveAccess("");
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Accounts & Access" },
    { label: "Project Management" },
  ];

  const tabsDataUsersPC = [
    {
      name: "Project Details",
      count: "",
      content: (
        <Box>
          <Box sx={{ display: "inline-block" }}>
            <SearchSelectProjectTextField
              txtFldStyle={styles.searchSelectTextField}
            />
          </Box>
          <Box
            sx={{
              maxHeight: 500,
              overflowY: "visible",
              overflowX: "visible",
            }}
          >
            <NewRequestTab
              selectedProjectDetails={selectedProject}
              projectStatus={project_status}
              moduleTypes={moduleTypes}
              accessTypes={accessTypes}
            />
          </Box>
        </Box>
      ),
    },
    {
      name: "Project Request",
      count: "",

      content: (
        <Box>
          <Box sx={styles.spContainer}>
            <Box sx={{ display: "inline-block" }}>
              <SearchSelectProjectTextField
                txtFldStyle={styles.searchSelectTextField}
              />
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <PendingRequestsTab
              selectedProjectDetails={selectedProject}
              moduleTypes={moduleTypes}
              accessModuleTypes={access_module_types}
              accessRequestTypes={access_request_types}
              accessRequestStatus={access_request_status}
            />
          </Box>
        </Box>
      ),
    },
    {
      name: "Access Control",
      count: "",

      content: (
        <Box>
          <Box sx={styles.spContainer}>
            <Box sx={{ display: "inline-block" }}>
              <SearchSelectProjectTextField
                txtFldStyle={styles.searchSelectTextField}
              />
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <AccessControlTab
              selectedProjectDetails={selectedProject}
              projectStatus={project_status}
              moduleTypes={moduleTypes}
              accessTypes={accessTypes}
            />
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        width: "100%",
        flexDirection: "column",
      }}
    >
      <Box sx={{ m: 1 }}>
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box sx={{ mt: 1, ...styles.spContainer }}>
        <CustomTabsV2
          value={selectedPaTab}
          onTabChange={(tabValue) => {
            setSelectedPaTab(tabValue);
            if (tabValue === 2) {
              setTriggerFetchUser((prev) => !prev);
            }
          }}
          tabs={tabsDataUsersPC}
          setTriggerFetchUser={setTriggerFetchUser}
        />
      </Box>

      <CustomDialogBoxV2
        open={openApproveAccessDialog}
        onClose={handleCancelApproveAccess}
        title={"Approve Access"}
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleConfirmApproveAccess}
        onClickReject={handleCancelApproveAccess}
        onConfirm={handleConfirmApproveAccess}
        actionButtonStyle={"continuous"}
      >
        <Stack spacing={1.2}>
          <DialogContentText>
            Are you sure you want to approve access?
          </DialogContentText>
          <Box>
            <CustomTextFieldN
              size={"small"}
              placeholder={"Reason to approve access*"}
              type={"text"}
              value={reasonToApproveAccess}
              setValue={setReasonToApproveAccess}
              setValueError={() => null}
              variant={"standard"}
              txtFldStyle={styles.txtStyle}
            />
          </Box>
        </Stack>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={openRejectAccessDialog}
        onClose={handleCancelRejectAccess}
        title={"Reject Access"}
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleConfirmRejectAccess}
        onClickReject={handleCancelRejectAccess}
        onConfirm={handleConfirmRejectAccess}
        actionButtonStyle={"continuous"}
      >
        <Stack spacing={1.2}>
          <DialogContentText>
            Are you sure you want to reject access?
          </DialogContentText>
          <Box>
            <CustomTextFieldN
              size={"small"}
              placeholder={"Reason to reject access*"}
              type={"text"}
              value={reasonToRejectAccess}
              setValue={setReasonToRejectAccess}
              setValueError={() => null}
              variant={"standard"}
              txtFldStyle={styles.txtStyle}
            />
          </Box>
        </Stack>
      </CustomDialogBoxV2>
      <Dialog open={openForwardApproveAccessDialog}>
        <DialogTitle>Forward Access For Approval</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.2}>
            {selectedRowData?.page_details?.length > 0 && (
              <Accordion style={{ marginBottom: "10px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Pages
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {selectedRowData?.page_details?.map((pageItem) => (
                      <Grid
                        item
                        xs={12}
                        key={pageItem?.page_id || pageItem?.page_name}
                      >
                        <Box display="flex" alignItems="center">
                          {pageItem?.page_name || "-"}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {selectedRowData?.feature_details?.length > 0 && (
              <Accordion style={{ marginBottom: "10px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Features
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.entries(
                    selectedRowData?.feature_details.reduce((acc, feature) => {
                      if (!acc[feature?.feature_name])
                        acc[feature?.feature_name] = [];
                      acc[feature?.feature_name].push(feature);
                      return acc;
                    }, {}),
                  ).map(([featureName, featureGroup]) => (
                    <Accordion
                      key={featureName}
                      style={{ marginBottom: "8px", marginLeft: "8px" }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body1" gutterBottom>
                          {featureName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {featureGroup.map((detail) => {
                            if (!detail || typeof detail !== "object")
                              return null;
                            return (
                              <Grid
                                item
                                xs={12}
                                key={detail?.feature_id || detail?.description}
                              >
                                <Box display="flex" alignItems="center">
                                  {detail?.description || "-"}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ ...appTheme.commonBtnStyle }}
            color="error"
            variant="contained"
            onClick={() => handleCloseApproveAccessDialog()}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectAccess;

const styles = {
  spContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },

  searchSelectTextField: {
    width: { xs: "100px", sm: "140px", md: "180px" },
    fontSize: { xs: "0.7rem", sm: "0.8rem" },
    borderRadius: "0",
    "& .MuiInputBase-input": {
      color: "#000000",
    },

    "& input:-webkit-autofill": {
      WebkitTextFillColor: "#000000",
      WebkitBoxShadow: "0 0 0px 1000px white inset",
      transition: "background-color 5000s ease-in-out 0s",
      transitionDelay: "9999s",
    },

    "& .MuiOutlinedInput-root": {
      height: "32px",
      "& fieldset": { borderColor: "#D0D0CE", borderRadius: "0px" },
      "&:hover fieldset": { borderColor: "#D0D0CE" },
      "&.Mui-focused fieldset": { borderColor: "#000000" },
      "&.Mui-disabled fieldset": { borderColor: "#D0D0CE" },
      "&.Mui-error fieldset": { borderColor: "#e53935" },

      "&:hover .MuiInputBase-input::placeholder": { color: "#848587" },
      "&.Mui-focused .MuiInputBase-input::placeholder": { color: "#232323" },
    },
    "& .MuiInputBase-input, & .MuiSelect-select": {
      color: "#000000 !important",
      WebkitTextFillColor: "#000000 !important",
    },

    "& .MuiSelect-icon": {
      color: "#000000",
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },

    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },

    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      marginLeft: 0,
      marginTop: "4px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "black",
    },
  },

  tabsContainer: {
    display: "flex",
  },
};
